import { useState, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import logo from "../traderhub24-logo.svg";
import { useAuth } from './contexts/AuthContext'
import { useMembership } from './hooks/useMembership'
import { supabase } from './lib/supabase'
import Login from './components/Login'
import Register from './components/Register'
import Footer from './components/Footer'
import { currencies } from './components/CurrencySelector'

const CURRENCY_STORAGE_KEY = 'traderhub24_selected_currency';

interface Trade {
  id: string
  market: 'Commodity' | 'Crypto' | 'Forex' | 'Stock'
  symbol: string
  exchange?: string | null
  trade_type: 'spot' | 'future'
  long_short: 'long' | 'short'
  quantity: number | null
  contract_size: number | null
  entry_date: string | null
  entry_price: number
  stop_loss: number
  take_profit: number
  rr: number | null
  exit_date: string | null
  exit_price: number | null
  trade_setup: string | null
  notes: string | null
  strategy: string | null
  strategy_id: string | null
  currency: string | null
  position_size: number | null
  pnl_percentage: number | null
  pnl: number | null
  total_entry_amount?: number | null
  total_exit_amount?: number | null
  time_interval: string | null
  unrealized_pnl?: number | null
  created_at: string
  updated_at: string
}

interface Strategy {
  id: string
  display_name: string
}

function Analytics({ onBack, setCurrentPage, embedded }: { onBack: () => void; setCurrentPage: (page: "home" | "changelog" | "all-trades" | "analytics" | "tools" | "strategies" | "social-trade" | "account" | "community") => void; embedded?: boolean }) {
  const { t } = useTranslation(['trading', 'common'])
  const navigate = useNavigate()
  const { user, signOut, loading: authLoading } = useAuth()
  const { membershipType } = useMembership()
  const [showLogin, setShowLogin] = useState(false)
  const [showRegister, setShowRegister] = useState(false)
  const [trades, setTrades] = useState<Trade[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tradeListRefresh, setTradeListRefresh] = useState(0)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const [selectedRange, setSelectedRange] = useState<'all' | '1d' | '1w' | '1m' | '3m' | '6m' | 'ytd' | '1y' | 'custom'>('ytd')
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [selectedStrategyId, setSelectedStrategyId] = useState<string>('')
  const [filterMarket, setFilterMarket] = useState<string>('all')
  const [filterExchange, setFilterExchange] = useState<string>('all')
  const [filterLongShort, setFilterLongShort] = useState<string>('all')
  const [filterTimeInterval, setFilterTimeInterval] = useState<string>('all')
  const [strategies, setStrategies] = useState<Strategy[]>([])
  const [strategiesLoading, setStrategiesLoading] = useState(true)
  const [selectedCurrency, setSelectedCurrency] = useState<string>(() => {
    const stored = localStorage.getItem(CURRENCY_STORAGE_KEY);
    return stored || 'USD';
  })
  const [exchangeRates, setExchangeRates] = useState<Map<string, number>>(new Map())
  const [exchangeRatesLoading, setExchangeRatesLoading] = useState(true)
  // Listen for currency changes from CurrencySelector in Footer
  useEffect(() => {
    const handleStorageChange = () => {
      const stored = localStorage.getItem(CURRENCY_STORAGE_KEY);
      if (stored) {
        setSelectedCurrency(stored);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    // Also listen to custom event for same-tab updates
    window.addEventListener('currencyChanged', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('currencyChanged', handleStorageChange);
    };
  }, []);
  const rangeOptions: { label: string; value: typeof selectedRange }[] = [
    { label: 'All', value: 'all' },
    { label: 'D', value: '1d' },
    { label: 'W', value: '1w' },
    { label: 'M', value: '1m' },
    { label: '3M', value: '3m' },
    { label: '6M', value: '6m' },
    { label: 'YTD', value: 'ytd' },
    { label: 'Y', value: '1y' },
  ]

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false)
      }
    }

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showUserMenu])

  // Session control - redirect to login if not authenticated (skip when embedded; layout handles auth)
  useEffect(() => {
    if (!embedded && !authLoading && !user) {
      setShowLogin(true)
    }
  }, [embedded, authLoading, user])

  const fetchTrades = async () => {
    if (!user) {
      setTrades([])
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('trades_with_market_data')
        .select('*')
        .eq('user_id', user.id)
        .order('entry_date', { ascending: false })

      if (error) throw error

      const baseTrades = (data || []) as Trade[]

      // Hydrate `exchange` from `symbols.exchange` (best-effort lookup by symbol + market).
      try {
        const uniqueSymbols = Array.from(
          new Set(baseTrades.map((t) => t.symbol).filter(Boolean))
        )
        const uniqueMarkets = Array.from(
          new Set(baseTrades.map((t) => t.market).filter(Boolean))
        )

        if (uniqueSymbols.length > 0) {
          let symbolsQuery = supabase
            .from('symbols')
            .select('symbol, market, exchange')
            .in('symbol', uniqueSymbols)

          if (uniqueMarkets.length > 0) {
            symbolsQuery = symbolsQuery.in('market', uniqueMarkets)
          }

          const { data: symbolsData, error: symbolsError } = await symbolsQuery

          if (!symbolsError && symbolsData) {
            const exchangeByKey = new Map<string, string | null>()
            for (const s of symbolsData as Array<{ symbol: string; market: string; exchange: string | null }>) {
              const key = `${s.market}::${s.symbol}`.toUpperCase()
              if (!exchangeByKey.has(key) || (exchangeByKey.get(key) == null && s.exchange != null)) {
                exchangeByKey.set(key, s.exchange)
              }
            }

            const hydrated = baseTrades.map((t) => {
              const key = `${t.market}::${t.symbol}`.toUpperCase()
              return { ...t, exchange: exchangeByKey.get(key) ?? null }
            })
            setTrades(hydrated)
          } else {
            setTrades(baseTrades)
          }
        } else {
          setTrades(baseTrades)
        }
      } catch (hydrateErr) {
        console.warn('Failed to hydrate exchanges from symbols:', hydrateErr)
        setTrades(baseTrades)
      }
      setError(null)
    } catch (err: any) {
      setError(err.message || 'Failed to load trades')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (user) {
      fetchTrades()
      fetchStrategies()
      fetchExchangeRates()
    }
  }, [user, tradeListRefresh])

  // Fetch exchange rates for currency conversion
  const fetchExchangeRates = async () => {
    if (!user) return
    
    try {
      setExchangeRatesLoading(true)
      // Get all unique exit dates from filtered trades that need conversion
      const closedTradesWithCurrency = filteredTrades.filter(
        t => t.exit_date && t.pnl !== null && t.currency && t.currency !== selectedCurrency
      )
      
      if (closedTradesWithCurrency.length === 0) {
        setExchangeRatesLoading(false)
        return
      }

      // Create a map of unique (date, currency) pairs
      const dateCurrencyPairs = new Map<string, { date: string; currency: string }>()
      closedTradesWithCurrency.forEach(trade => {
        if (!trade.exit_date || !trade.currency) return
        const date = trade.exit_date.split('T')[0]
        const key = `${date}:${trade.currency}`
        if (!dateCurrencyPairs.has(key)) {
          dateCurrencyPairs.set(key, { date, currency: trade.currency })
        }
      })

      // Fetch exchange rates for each unique (date, currency) pair
      const ratesMap = new Map<string, number>()
      const displayCurr = displayCurrency
      
      for (const { date, currency: tradeCurrency } of dateCurrencyPairs.values()) {
        // Skip if already in cache
        if (ratesMap.has(`${date}:${displayCurr}:${tradeCurrency}`) || 
            ratesMap.has(`${date}:${tradeCurrency}:${displayCurr}`)) {
          continue
        }
        
        // Try direct pair: displayCurrency/trade.currency
        let { data: rateData, error } = await supabase
          .from('exchange_rates')
          .select('rate')
          .eq('base_currency', displayCurr)
          .eq('target_currency', tradeCurrency)
          .eq('rate_date', date)
          .limit(1)
          .maybeSingle()

        // If not found, try to find nearest date (lte = less than or equal)
        if (error || !rateData) {
          const { data: nearestData } = await supabase
            .from('exchange_rates')
            .select('rate')
            .eq('base_currency', displayCurr)
            .eq('target_currency', tradeCurrency)
            .lte('rate_date', date)
            .order('rate_date', { ascending: false })
            .limit(1)
            .maybeSingle()

          if (nearestData) {
            rateData = nearestData
          } else {
            // Try reverse pair: trade.currency/displayCurrency
            const { data: reverseData } = await supabase
              .from('exchange_rates')
              .select('rate')
              .eq('base_currency', tradeCurrency)
              .eq('target_currency', displayCurr)
              .lte('rate_date', date)
              .order('rate_date', { ascending: false })
              .limit(1)
              .maybeSingle()

            if (reverseData) {
              // Invert the rate: if we have TRY/USD, we need USD/TRY = 1 / (TRY/USD)
              rateData = { rate: 1 / parseFloat(reverseData.rate.toString()) }
            }
          }
        }

        if (rateData) {
          const key = `${date}:${displayCurr}:${tradeCurrency}`
          ratesMap.set(key, parseFloat(rateData.rate.toString()))
        }
      }

      setExchangeRates(ratesMap)
    } catch (err: any) {
      console.error('Failed to fetch exchange rates:', err)
    } finally {
      setExchangeRatesLoading(false)
    }
  }

  // Convert P&L to display currency using exchange rate
  const convertPnl = (trade: Trade): number => {
    if (!trade.pnl || !trade.exit_date || !trade.currency) {
      return trade.pnl || 0
    }

    // If trade currency matches display currency, no conversion needed
    if (trade.currency === displayCurrency) {
      return trade.pnl
    }

    // Get exchange rate for trade's exit date
    const date = trade.exit_date.split('T')[0] // Get date part only
    
    // Try direct pair: displayCurrency/trade.currency
    let rate = exchangeRates.get(`${date}:${displayCurrency}:${trade.currency}`)
    
    if (rate) {
      // If we have displayCurrency/tradeCurrency rate: 1 displayCurrency = rate tradeCurrency
      // To convert: tradePnl / rate = displayCurrencyPnl
      return trade.pnl / rate
    }

    // Try reverse pair: trade.currency/displayCurrency
    const reverseRate = exchangeRates.get(`${date}:${trade.currency}:${displayCurrency}`)
    
    if (reverseRate) {
      // If we have tradeCurrency/displayCurrency rate: 1 tradeCurrency = rate displayCurrency
      // To convert: tradePnl * rate = displayCurrencyPnl
      return trade.pnl * reverseRate
    }

    // If no rate found in cache, return original P&L (fallback)
    // Note: fetchExchangeRates should have already fetched the nearest date rate
    console.warn(`No exchange rate found in cache for ${trade.currency} to ${displayCurrency} on ${date}`)
    return trade.pnl
  }

  // Initialize YTD dates on component mount
  useEffect(() => {
    const today = new Date()
    const ytdStart = new Date(today.getFullYear(), 0, 1)
    setStartDate(formatDate(ytdStart))
    setEndDate(formatDate(today))
  }, [])

  const fetchStrategies = async () => {
    if (!user) return

    try {
      setStrategiesLoading(true)
      const { data, error } = await supabase
        .from('strategies')
        .select('id, display_name')
        .or(`user_id.eq.${user.id},is_public.eq.true`)
        .eq('is_active', true)
        .order('display_name', { ascending: true })

      if (error) throw error

      setStrategies(data || [])
    } catch (err: any) {
      console.error('Failed to load strategies:', err)
    } finally {
      setStrategiesLoading(false)
    }
  }

  const formatDate = (date: Date) => {
    return date.toISOString().split('T')[0]
  }

  const applyQuickRange = (range: typeof selectedRange) => {
    const today = new Date()
    let start = ''
    let end = formatDate(today)

    switch (range) {
      case '1d': {
        const d = new Date(today)
        d.setDate(d.getDate() - 1)
        start = formatDate(d)
        break
      }
      case '1w': {
        const d = new Date(today)
        d.setDate(d.getDate() - 7)
        start = formatDate(d)
        break
      }
      case '1m': {
        const d = new Date(today)
        d.setMonth(d.getMonth() - 1)
        start = formatDate(d)
        break
      }
      case '3m': {
        const d = new Date(today)
        d.setMonth(d.getMonth() - 3)
        start = formatDate(d)
        break
      }
      case '6m': {
        const d = new Date(today)
        d.setMonth(d.getMonth() - 6)
        start = formatDate(d)
        break
      }
      case 'ytd': {
        const d = new Date(today.getFullYear(), 0, 1)
        start = formatDate(d)
        break
      }
      case '1y': {
        const d = new Date(today)
        d.setFullYear(d.getFullYear() - 1)
        start = formatDate(d)
        break
      }
      case 'all':
        start = ''
        end = ''
        break
      default:
        break
    }

    setSelectedRange(range)
    setStartDate(start)
    setEndDate(end)
  }

  const isWithinRange = (dateStr: string | null) => {
    if (!dateStr) return false
    const date = new Date(dateStr)
    if (Number.isNaN(date.getTime())) return false

    if (selectedRange === 'all' && !startDate && !endDate) {
      return true
    }

    if (startDate) {
      const start = new Date(startDate)
      if (date < start) return false
    }

    if (endDate) {
      const end = new Date(endDate)
      end.setHours(23, 59, 59, 999)
      if (date > end) return false
    }

    return true
  }

  const canonicalizeExchange = (raw: string | null | undefined): string | null => {
    if (!raw) return null
    const trimmed = raw.trim()
    if (!trimmed) return null
    const upper = trimmed.toUpperCase()

    // Group US exchanges under one option
    const isNasdaq =
      upper === 'NASDAQ' ||
      upper.includes('NASDAQ STOCK') ||
      upper.includes('NASDAQ GLOBAL') ||
      upper.includes('NASDAQ CAPITAL') ||
      upper.includes('NASDAQ GS') ||
      upper.includes('NASDAQ GM')
    const isNyse =
      upper === 'NYSE' ||
      upper.includes('NEW YORK STOCK EXCHANGE') ||
      upper.includes('NYSE AMERICAN') ||
      upper.includes('NYSE ARCA')

    if (isNasdaq || isNyse) return 'US Exchanges'

    return trimmed
  }

  const filteredTrades = useMemo(() => {
    return trades.filter(trade => {
      const dateForFilter = trade.exit_date || trade.entry_date
      const dateMatches = isWithinRange(dateForFilter)
      
      // Filter by strategy if one is selected
      if (selectedStrategyId) {
        if (!(dateMatches && trade.strategy_id === selectedStrategyId)) return false
      } else if (!dateMatches) {
        return false
      }

      if (filterMarket !== 'all' && trade.market !== filterMarket) return false

      if (filterExchange !== 'all') {
        const canon = canonicalizeExchange(trade.exchange)
        if ((canon || '').toLowerCase() !== filterExchange.toLowerCase()) return false
      }

      if (filterLongShort !== 'all' && trade.long_short !== filterLongShort) return false

      if (filterTimeInterval !== 'all' && trade.time_interval !== filterTimeInterval) return false

      return true
    })
  }, [trades, selectedRange, startDate, endDate, selectedStrategyId, filterMarket, filterExchange, filterLongShort, filterTimeInterval])

  // Determine display currency based on filtered trades
  const displayCurrency = useMemo(() => {
    const closedTradesForCurrency = filteredTrades.filter(trade => trade.exit_date && trade.pnl !== null && trade.currency)
    
    if (closedTradesForCurrency.length === 0) {
      return selectedCurrency
    }

    // Get unique currencies from filtered trades
    const uniqueCurrencies = Array.from(new Set(closedTradesForCurrency.map(t => t.currency).filter(Boolean)))
    
    // If all trades have the same currency, use that
    if (uniqueCurrencies.length === 1) {
      return uniqueCurrencies[0] as string
    }
    
    // If mixed currencies, use selected currency from CurrencySelector (in Footer)
    return selectedCurrency
  }, [filteredTrades, selectedCurrency])

  // Refetch exchange rates when currency or filtered trades change
  useEffect(() => {
    if (user && filteredTrades.length > 0) {
      fetchExchangeRates()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCurrency, filteredTrades])

  // Calculate analytics with currency conversion
  const closedTrades = filteredTrades.filter(trade => trade.exit_date && trade.pnl !== null)
  const openTrades = filteredTrades.filter(trade => !trade.exit_date)
  
  // Convert P&L values to display currency
  const closedTradesWithConvertedPnl = closedTrades.map(trade => ({
    ...trade,
    convertedPnl: convertPnl(trade)
  }))
  
  const totalTrades = closedTradesWithConvertedPnl.length
  const winningTrades = closedTradesWithConvertedPnl.filter(trade => trade.convertedPnl > 0)
  const losingTrades = closedTradesWithConvertedPnl.filter(trade => trade.convertedPnl < 0)
  const breakEvenTrades = closedTradesWithConvertedPnl.filter(trade => trade.convertedPnl === 0)
  
  const winRate = totalTrades > 0 ? (winningTrades.length / totalTrades) * 100 : 0
  
  const totalProfit = winningTrades.reduce((sum, trade) => sum + trade.convertedPnl, 0)
  const totalLoss = Math.abs(losingTrades.reduce((sum, trade) => sum + trade.convertedPnl, 0))
  const profitFactor = totalLoss > 0 ? totalProfit / totalLoss : (totalProfit > 0 ? Infinity : 0)
  
  const totalPnl = closedTradesWithConvertedPnl.reduce((sum, trade) => sum + trade.convertedPnl, 0)

  const pnlPctTrades = closedTrades.filter(trade => trade.pnl_percentage !== null)
  const avgPnlPct = pnlPctTrades.length > 0 
    ? pnlPctTrades.reduce((sum, trade) => sum + (trade.pnl_percentage || 0), 0) / pnlPctTrades.length 
    : null
  
  // Use total_entry_amount for weighting (position_size is 0 for closed trades after recent update)
  const weightSize = (trade: Trade) => (trade.total_entry_amount ?? trade.position_size ?? 0)
  const weightedPnlTrades = closedTrades.filter(
    trade => trade.pnl_percentage !== null && weightSize(trade) > 0
  )
  const totalPositionSize = weightedPnlTrades.reduce((sum, trade) => sum + weightSize(trade), 0)
  const weightedPnlPct = totalPositionSize > 0
    ? weightedPnlTrades.reduce(
        (sum, trade) => sum + weightSize(trade) * (trade.pnl_percentage || 0),
        0
      ) / totalPositionSize
    : null

  const avgWin = winningTrades.length > 0 ? totalProfit / winningTrades.length : 0
  const avgLoss = losingTrades.length > 0 ? totalLoss / losingTrades.length : 0
  const avgRR = avgLoss > 0 ? avgWin / avgLoss : 0

  // Expectancy = (Win rate × Avg Win R) – (Loss rate × Avg Loss R), using realized R
  const computeRealizedR = (trade: Trade): number | null => {
    const entry = trade.entry_price
    const exit = trade.exit_price
    const sl = trade.stop_loss
    const ls = (trade.long_short || '').toLowerCase()
    if (entry == null || exit == null || sl == null) return null
    const risk = ls === 'long' ? entry - sl : sl - entry
    if (risk <= 0) return null
    const reward = ls === 'long' ? exit - entry : entry - exit
    return reward / risk
  }
  const winningWithR = winningTrades.filter(t => computeRealizedR(t) !== null)
  const losingWithR = losingTrades.filter(t => computeRealizedR(t) !== null)
  const tradesWithRCount = winningWithR.length + losingWithR.length
  const avgWinR = winningWithR.length > 0
    ? winningWithR.reduce((s, t) => s + (computeRealizedR(t) ?? 0), 0) / winningWithR.length
    : 0
  const avgLossR = losingWithR.length > 0
    ? losingWithR.reduce((s, t) => s + Math.abs(computeRealizedR(t) ?? 0), 0) / losingWithR.length
    : 0
  const winRateForExp = tradesWithRCount > 0 ? winningWithR.length / tradesWithRCount : 0
  const lossRateForExp = tradesWithRCount > 0 ? losingWithR.length / tradesWithRCount : 0
  const expectancy = tradesWithRCount > 0
    ? winRateForExp * avgWinR - lossRateForExp * avgLossR
    : null

  // Planned R vs Realized R: average(realizedR - plannedR) for trades with both rr and computed R
  const tradesWithPlannedAndRealized = closedTrades.filter(t => {
    const realized = computeRealizedR(t)
    return realized !== null && t.rr != null && t.rr > 0
  })
  const plannedVsRealizedDiff = tradesWithPlannedAndRealized.length > 0
    ? tradesWithPlannedAndRealized.reduce((sum, t) => sum + ((computeRealizedR(t) ?? 0) - (t.rr ?? 0)), 0) / tradesWithPlannedAndRealized.length
    : null

  // Stop olan trade oranı: long için exit < sl, short için exit > sl (stop'a değen işlemler)
  const tradesThatHitStop = closedTrades.filter(t => {
    if (t.exit_price == null || t.stop_loss == null) return false
    return t.long_short === 'long' ? t.exit_price < t.stop_loss : t.exit_price > t.stop_loss
  })
  const stopHitRate = totalTrades > 0 ? (tradesThatHitStop.length / totalTrades) * 100 : null

  // Exit-SL farkı (% cinsinden): ortalama (exit - sl) / sl * 100. Pozitif = SL üstünde çıkış, negatif = stop'a takıldı
  const computeExitSlDiffPct = (trade: Trade): number | null => {
    const exit = trade.exit_price
    const sl = trade.stop_loss
    const ls = (trade.long_short || '').toLowerCase()
    if (exit == null || sl == null || sl === 0) return null
    return ls === 'long' ? ((exit - sl) / sl) * 100 : ((sl - exit) / sl) * 100
  }
  const tradesWithExitSl = closedTrades.filter(t => computeExitSlDiffPct(t) !== null)
  const avgExitSlDiffPct = tradesWithExitSl.length > 0
    ? tradesWithExitSl.reduce((sum, t) => sum + (computeExitSlDiffPct(t) ?? 0), 0) / tradesWithExitSl.length
    : null
  
  const bestTrade = closedTradesWithConvertedPnl.length > 0 
    ? closedTradesWithConvertedPnl.reduce((best, trade) => trade.convertedPnl > best.convertedPnl ? trade : best, closedTradesWithConvertedPnl[0])
    : null
  
  const worstTrade = closedTradesWithConvertedPnl.length > 0
    ? closedTradesWithConvertedPnl.reduce((worst, trade) => trade.convertedPnl < worst.convertedPnl ? trade : worst, closedTradesWithConvertedPnl[0])
    : null

  // Market breakdown
  const marketBreakdown = closedTradesWithConvertedPnl.reduce((acc, trade) => {
    const market = trade.market
    if (!acc[market]) {
      acc[market] = { count: 0, pnl: 0, wins: 0, losses: 0 }
    }
    acc[market].count++
    acc[market].pnl += trade.convertedPnl
    if (trade.convertedPnl > 0) acc[market].wins++
    if (trade.convertedPnl < 0) acc[market].losses++
    return acc
  }, {} as Record<string, { count: number; pnl: number; wins: number; losses: number }>)

  // Long/Short breakdown
  const longShortBreakdown = closedTradesWithConvertedPnl.reduce((acc, trade) => {
    const direction = trade.long_short
    if (!acc[direction]) {
      acc[direction] = { count: 0, pnl: 0, wins: 0, losses: 0 }
    }
    acc[direction].count++
    acc[direction].pnl += trade.convertedPnl
    if (trade.convertedPnl > 0) acc[direction].wins++
    if (trade.convertedPnl < 0) acc[direction].losses++
    return acc
  }, {} as Record<string, { count: number; pnl: number; wins: number; losses: number }>)

  const canonicalizeExchangeForOptions = (raw: string | null | undefined): string | null => {
    return canonicalizeExchange(raw)
  }

  const availableExchanges = Array.from(
    new Set(
      trades
        .map((t) => canonicalizeExchangeForOptions(t.exchange))
        .filter((x): x is string => !!x && x.trim().length > 0)
    )
  ).sort((a, b) => {
    if (a === 'US Exchanges' && b !== 'US Exchanges') return -1
    if (b === 'US Exchanges' && a !== 'US Exchanges') return 1
    return a.localeCompare(b)
  })

  const availableTimeIntervals = Array.from(
    new Set(
      trades
        .map((t) => t.time_interval)
        .filter((x): x is string => !!x && x.trim().length > 0)
    )
  ).sort((a, b) => a.localeCompare(b))

  const formatCurrency = (amount: number | null, currency: string | null = null) => {
    if (amount === null) return '-'
    const formatted = amount.toFixed(2).replace('.', ',')
    const currencyToUse = currency || displayCurrency
    return currencyToUse ? `${formatted} ${currencyToUse}` : formatted
  }

  const formatPercent = (value: number | null) => {
    if (value === null) return '-'
    return `${value.toFixed(2)}%`
  }

  const createDetailId = (trade: Trade) => {
    const uuid = crypto.randomUUID ? crypto.randomUUID() : `detail-${Math.random().toString(36).slice(2, 10)}`
    return `${trade.symbol}-${uuid}`
  }

  const buildDetailState = (trade: Trade) => {
    const { id: _tradeId, ...restWithUser } = trade as Trade & { user_id?: string }
    const { user_id: _userId, ...tradePayload } = restWithUser
    const baseDate = trade.entry_date ? new Date(trade.entry_date) : new Date()
    const entryPrice = trade.entry_price
    const exitPrice = trade.exit_price ?? (trade.long_short === 'long' ? entryPrice * 1.02 : entryPrice * 0.98)
    const timeline = [
      { date: baseDate.toISOString(), title: 'Trade Opened', description: 'Initial entry placed.', price: entryPrice, tag: 'entry' },
      { date: new Date(baseDate.getTime() + 24 * 60 * 60 * 1000).toISOString(), title: 'Daily Review', description: 'Reviewed stop placement.', price: entryPrice * (trade.long_short === 'long' ? 1.01 : 0.99), tag: 'review' },
      { date: new Date(baseDate.getTime() + 2 * 24 * 60 * 60 * 1000).toISOString(), title: 'Market Event', description: 'Macro headline created volatility.', tag: 'macro' },
      { date: new Date(baseDate.getTime() + 3 * 24 * 60 * 60 * 1000).toISOString(), title: 'Adjustment', description: 'Moved stop to breakeven.', price: entryPrice, tag: 'risk' },
      { date: new Date(baseDate.getTime() + 4 * 24 * 60 * 60 * 1000).toISOString(), title: trade.exit_date ? 'Trade Closed' : 'Current Status', description: trade.exit_date ? 'Position exited.' : 'Position still active.', price: exitPrice, tag: trade.exit_date ? 'exit' : 'open' },
    ]
    const comments = [
      { author: 'Self-Note', text: 'Keep journaling intraday emotions.', date: baseDate.toISOString(), mood: 'Focused' },
      { author: 'Market', text: 'Watch for liquidity sweep.', date: new Date(baseDate.getTime() + 2 * 24 * 60 * 60 * 1000).toISOString(), mood: 'Alert' },
    ]
    return { trade: tradePayload, timeline, comments, mood: 'Focused', tradeId: trade.id }
  }

  const seededAllowHistory = (seed: string) => {
    let hash = 0
    for (let i = 0; i < seed.length; i++) {
      hash = (hash << 5) - hash + seed.charCodeAt(i)
      hash |= 0
    }
    return ((hash >>> 0) / 4294967295) < 0.2
  }

  const handleNavigateToDetail = (trade: Trade, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const detailId = createDetailId(trade)
    const detailState = buildDetailState(trade)
    const historyAccessGranted = membershipType === 'pro' ? true : membershipType === 'free' ? seededAllowHistory(detailId) : false
    navigate(`/app/trade/${detailId}`, { state: { ...detailState, historyAccessGranted } })
  }

  // If not authenticated, show login prompt (skip when embedded; layout handles auth)
  if (!user && !authLoading && !embedded) {
    return (
      <div className="min-h-screen bg-app-bg text-gray-900">
        {/* Header */}
        <header className="w-full flex justify-between items-center py-6 px-6 max-w-6xl mx-auto">
          <div className="flex items-center space-x-2">
            <img 
              src={logo} 
              alt="TraderHub24" 
              className="h-20 w-auto cursor-pointer hover:opacity-80 transition-opacity" 
              onClick={onBack}
            />
          </div>
          <div className="flex items-center gap-2">
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-6xl mx-auto px-6 py-6">
          <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-6">
            <p className="text-gray-500 text-center mb-4">Please sign in to view your analytics</p>
            <div className="flex justify-center gap-2">
              <button
                onClick={() => setShowLogin(true)}
                className="rounded-full border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Sign In
              </button>
              <button
                onClick={() => setShowRegister(true)}
                className="rounded-full bg-orange-500 hover:bg-orange-600 px-4 py-2 text-sm font-medium text-white transition-colors"
              >
                Sign Up
              </button>
            </div>
          </div>
        </main>

        {/* Footer */}
        <Footer currentPage="analytics" onNavigate={(page) => {
          if (page === "privacy-policy") navigate("/privacy");
          else if (page === "terms-and-conditions") navigate("/terms");
          else if (page === "risk-warning") navigate("/risk-warning");
          else if (page === "documentation") navigate("/documentation");
          else if (page === "contact") navigate("/contact");
          else if (page === "home") onBack();
          else if (page === "changelog") navigate("/changelog");
        }} />

        {/* Auth Modals */}
        {showLogin && (
          <Login
            onClose={() => setShowLogin(false)}
            onSwitchToRegister={() => {
              setShowLogin(false)
              setShowRegister(true)
            }}
          />
        )}
        {showRegister && (
          <Register
            onClose={() => setShowRegister(false)}
            onSwitchToLogin={() => {
              setShowRegister(false)
              setShowLogin(true)
            }}
          />
        )}
      </div>
    )
  }

  return (
    <div className={embedded ? "w-full" : "min-h-screen bg-app-bg text-gray-900"}>
      {!embedded && (
      <header className="w-full py-3 px-4 md:px-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <img 
            src={logo} 
            alt="TraderHub24" 
            className="h-12 md:h-20 w-auto cursor-pointer hover:opacity-80 transition-opacity" 
            onClick={onBack}
          />
          <div className="flex items-center gap-2">
            {authLoading ? (
              <div className="text-sm text-gray-500">Loading...</div>
            ) : user ? (
              <div className="relative flex items-center gap-2">
                {/* Mobile Menu Button */}
                <button
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  className="md:hidden p-2 text-gray-700 hover:bg-gray-100 rounded-lg border border-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {showMobileMenu ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                    )}
                  </svg>
                </button>

                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="px-3 md:px-4 py-2 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-2 border border-gray-300"
                    title="Account"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span className="hidden md:inline">Account</span>
                  </button>
                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-64 bg-app-box rounded-lg shadow-lg border border-gray-200 z-50">
                      <div className="p-4 border-b border-gray-200">
                        <p className="text-sm font-semibold text-gray-900">Account</p>
                        <p className="text-sm text-gray-600 mt-1 truncate">{user.email}</p>
                      </div>
                      <div className="p-2">
                        <button
                          onClick={() => {
                            navigate("/app/account")
                            setShowUserMenu(false)
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4a4 4 0 100 8 4 4 0 000-8zM6 20a6 6 0 0112 0" />
                          </svg>
                          Account Settings
                        </button>
                        <button
                          onClick={() => {
                            signOut();
                            setShowUserMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          Sign Out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowLogin(true)}
                className="px-4 py-2 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-2 border border-gray-300"
                title="Sign In"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
                Sign In
              </button>
            )}
          </div>
        </div>

        {/* Mobile Sidebar/Menu */}
        {showMobileMenu && user && (
          <div className="md:hidden fixed inset-0 z-[100] flex">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowMobileMenu(false)} />
            <div className="relative w-64 max-w-[80%] h-full bg-app-box shadow-xl flex flex-col p-4 animate-in slide-in-from-left duration-300">
              <div className="flex items-center justify-between mb-8">
                <img src={logo} alt="TraderHub24" className="h-8 w-auto" />
                <button onClick={() => setShowMobileMenu(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <nav className="space-y-2">
                {[
                  { id: "all-trades", label: "All Trades", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" },
                  { id: "analytics", label: "Analytics", icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" },
                  { id: "tools", label: "Tools", icon: "M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a1 1 0 01-1-1v-3a1 1 0 011-1h1a2 2 0 100-4H4a1 1 0 01-1-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" },
                  { id: "strategies", label: "Strategies", icon: "M13 10V3L4 14h7v7l9-11h-7z" },
                  { id: "social-trade", label: "Social Trade", icon: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" },
                  { id: "community", label: "Community", icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" },
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setCurrentPage(item.id as any);
                      setShowMobileMenu(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      item.id === "analytics" 
                        ? "bg-orange-50 text-orange-600" 
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                    </svg>
                    {item.label}
                  </button>
                ))}
                <button
                  onClick={() => {
                    navigate('/app/add-trade');
                    setShowMobileMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium bg-orange-500 text-white hover:bg-orange-600 transition-colors mt-4"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Trade
                </button>
              </nav>
            </div>
          </div>
        )}
      </header>
      )}

      {(() => {
        const W = embedded ? 'div' : 'main';
        return (
        <W className={embedded ? 'dashboard-page-content' : 'max-w-6xl mx-auto px-4 md:px-6 py-6'}>
        {!embedded && (
        <section className="hidden md:flex items-center justify-center gap-4 mb-6">
          <button
            onClick={() => navigate("/app")}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors flex items-center justify-center"
            title="Home"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </button>
          <button
            onClick={() => navigate("/app/all-trades")}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
          >
            All Trades
          </button>
          <button
            onClick={() => navigate("/app/analytics")}
            className="px-6 py-2 text-sm font-medium text-white bg-orange-500 border border-orange-500 rounded-lg hover:bg-orange-600 transition-colors"
          >
            Analytics
          </button>
          <button
            onClick={() => navigate("/app/tools")}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
          >
            Tools
          </button>
          <button
            onClick={() => navigate("/app/strategies")}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
          >
            Strategies
          </button>
          <button
            onClick={() => navigate("/app/social-trade")}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
          >
            Social Trade
          </button>
          <button
            onClick={() => navigate("/app/community")}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
          >
            Community
          </button>
          {user && (
            <button
              onClick={() => navigate('/app/add-trade')}
              className="px-6 py-2 text-sm font-medium text-white bg-orange-500 border border-orange-500 rounded-lg hover:bg-orange-600 hover:border-orange-600 transition-colors"
            >
              Add Trade
            </button>
          )}
        </section>
        )}

        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold">{t('analytics.title')}</h1>
          <p className="text-sm md:text-gray-600 mt-2">{t('analytics.subtitle')}</p>
          {exchangeRatesLoading && (
            <p className="text-xs text-gray-500 mt-2">Loading exchange rates...</p>
          )}
          <div className="mt-4 bg-app-box border border-gray-200 rounded-2xl p-4">
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap gap-2">
                {rangeOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => applyQuickRange(option.value)}
                    className={`flex-1 sm:flex-none px-3 py-1.5 text-xs md:text-sm font-medium rounded-lg border transition-colors ${
                      selectedRange === option.value
                        ? 'bg-orange-500 border-orange-500 text-white'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-orange-500 hover:text-orange-500'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs md:text-sm text-gray-500 whitespace-nowrap">From:</span>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      setSelectedRange('custom')
                      setStartDate(e.target.value)
                    }}
                    className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs md:text-sm text-gray-500 whitespace-nowrap">To:</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => {
                      setSelectedRange('custom')
                      setEndDate(e.target.value)
                    }}
                    className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
                <div className="flex items-center gap-2 sm:col-span-2 lg:col-span-2">
                  <span className="text-xs md:text-sm text-gray-500 whitespace-nowrap">Strategy:</span>
                  <select
                    value={selectedStrategyId}
                    onChange={(e) => setSelectedStrategyId(e.target.value)}
                    className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
                  >
                    <option value="">All Strategies</option>
                    {strategiesLoading ? (
                      <option disabled>Loading strategies...</option>
                    ) : (
                      strategies.map((strategy) => (
                        <option key={strategy.id} value={strategy.id}>
                          {strategy.display_name}
                        </option>
                      ))
                    )}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs md:text-sm text-gray-500 whitespace-nowrap">Market:</span>
                  <select
                    value={filterMarket}
                    onChange={(e) => setFilterMarket(e.target.value)}
                    className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
                  >
                    <option value="all">All Markets</option>
                    <option value="Stock">Stock</option>
                    <option value="Crypto">Crypto</option>
                    <option value="Forex">Forex</option>
                    <option value="Commodity">Commodity</option>
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs md:text-sm text-gray-500 whitespace-nowrap">Exchange:</span>
                  <select
                    value={filterExchange}
                    onChange={(e) => setFilterExchange(e.target.value)}
                    className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
                    disabled={availableExchanges.length === 0}
                    title={availableExchanges.length === 0 ? 'No exchange data available' : 'Filter by exchange'}
                  >
                    <option value="all">All Exchanges</option>
                    {availableExchanges.map((ex) => (
                      <option key={ex} value={ex}>
                        {ex}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs md:text-sm text-gray-500 whitespace-nowrap">Direction:</span>
                  <select
                    value={filterLongShort}
                    onChange={(e) => setFilterLongShort(e.target.value)}
                    className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
                  >
                    <option value="all">All Directions</option>
                    <option value="long">LONG</option>
                    <option value="short">SHORT</option>
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs md:text-sm text-gray-500 whitespace-nowrap">Timeframe:</span>
                  <select
                    value={filterTimeInterval}
                    onChange={(e) => setFilterTimeInterval(e.target.value)}
                    className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
                    disabled={availableTimeIntervals.length === 0}
                    title={availableTimeIntervals.length === 0 ? 'No timeframe data available' : 'Filter by timeframe'}
                  >
                    <option value="all">All Timeframes</option>
                    {availableTimeIntervals.map((tf) => (
                      <option key={tf} value={tf}>
                        {tf}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            {((selectedRange !== 'all' || startDate || endDate) || selectedStrategyId || filterMarket !== 'all' || filterExchange !== 'all' || filterLongShort !== 'all' || filterTimeInterval !== 'all') && (
              <p className="text-[10px] md:text-xs text-gray-500 mt-3 italic">
                Showing trades
                {startDate ? ` from ${startDate}` : ''}
                {startDate && endDate ? ' ' : ''}
                {endDate ? ` until ${endDate}` : !startDate && selectedRange !== 'all' ? ' for selected range' : ''}
                {selectedStrategyId ? ` · Strategy: ${strategies.find(s => s.id === selectedStrategyId)?.display_name || 'selected'}` : ''}
                {filterMarket !== 'all' ? ` · Market: ${filterMarket}` : ''}
                {filterExchange !== 'all' ? ` · Exchange: ${filterExchange}` : ''}
                {filterLongShort !== 'all' ? ` · Direction: ${filterLongShort.toUpperCase()}` : ''}
                {filterTimeInterval !== 'all' ? ` · Timeframe: ${filterTimeInterval}` : ''}
              </p>
            )}
          </div>
        </div>

        {loading ? (
          <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-6">
            <p className="text-gray-500 text-center py-8">Loading analytics...</p>
          </div>
        ) : error ? (
          <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-6">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          </div>
        ) : closedTrades.length === 0 ? (
          <div className="space-y-6">
            <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-6">
              <p className="text-gray-500 text-center py-8">
                No closed trades found. Analytics will be available once you close some trades.
              </p>
            </div>
            {filteredTrades.length > 0 && (
              <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
                <h3 className="text-base md:text-lg font-semibold mb-4">{t('analytics.filteredTrades')}</h3>
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 font-semibold text-gray-700 whitespace-nowrap">{t('tradeList.symbol')}</th>
                        <th className="px-3 py-2 font-semibold text-gray-700 whitespace-nowrap">{t('addTrade.entryDate')}</th>
                        <th className="px-3 py-2 font-semibold text-gray-700 whitespace-nowrap">{t('tradeList.status')}</th>
                        <th className="px-3 py-2 font-semibold text-gray-700 whitespace-nowrap">{t('tradeList.pnl')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {filteredTrades.map((trade) => {
                        const isOpen = !trade.exit_date
                        const pnlVal = isOpen ? trade.unrealized_pnl : convertPnl(trade)
                        return (
                          <tr
                            key={trade.id}
                            onClick={(e) => handleNavigateToDetail(trade, e)}
                            className="cursor-pointer hover:bg-gray-50 transition-colors"
                          >
                            <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">{trade.symbol}</td>
                            <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                              {trade.entry_date ? new Date(trade.entry_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <span className={`px-2 py-0.5 rounded text-xs font-semibold ${isOpen ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                                {isOpen ? t('common:status.open') : t('common:status.closed')}
                              </span>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {pnlVal != null ? (
                                <span className={pnlVal >= 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                                  {formatCurrency(pnlVal)}
                                </span>
                              ) : '-'}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
                <div className="text-xs md:text-sm text-gray-500 mb-1">{t('analytics.totalTrades')}</div>
                <div className="text-2xl md:text-3xl font-bold text-gray-900">{totalTrades}</div>
                <div className="text-[10px] md:text-xs text-gray-500 mt-1">{openTrades.length} open</div>
              </div>
              <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
                <div className="text-xs md:text-sm text-gray-500 mb-1">{t('analytics.winRate')}</div>
                <div className="text-2xl md:text-3xl font-bold text-orange-600">{winRate.toFixed(1)}%</div>
                <div className="text-[10px] md:text-xs text-gray-500 mt-1">
                  {winningTrades.length}W / {losingTrades.length}L{breakEvenTrades.length > 0 ? ` / ${breakEvenTrades.length}BE` : ''}
                </div>
              </div>
              <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
                <div className="text-xs md:text-sm text-gray-500 mb-1">{t('analytics.profitFactor')}</div>
                <div className="text-2xl md:text-3xl font-bold text-gray-900">
                  {profitFactor === Infinity ? '∞' : profitFactor.toFixed(2)}
                </div>
                <div className="text-[10px] md:text-xs text-gray-500 mt-1">
                  {formatCurrency(totalProfit)} / {formatCurrency(totalLoss)}
                </div>
              </div>
              <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
                <div className="text-xs md:text-sm text-gray-500 mb-1">Total P&L</div>
                <div className={`text-2xl md:text-3xl font-bold ${totalPnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(totalPnl)}
                </div>
                <div className="text-[10px] md:text-xs text-gray-500 mt-1">All closed trades</div>
              </div>
              <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
                <div className="text-xs md:text-sm text-gray-500 mb-1">Avg P&L %</div>
                <div className={`text-2xl md:text-3xl font-bold ${avgPnlPct !== null && avgPnlPct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {avgPnlPct !== null ? formatPercent(avgPnlPct) : '-'}
                </div>
                <div className="text-[10px] md:text-xs text-gray-500 mt-1">
                  {pnlPctTrades.length} trades
                </div>
              </div>
              <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
                <div className="text-xs md:text-sm text-gray-500 mb-1">Weighted P&L %</div>
                <div className={`text-2xl md:text-3xl font-bold ${weightedPnlPct !== null && weightedPnlPct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {weightedPnlPct !== null ? formatPercent(weightedPnlPct) : '-'}
                </div>
                <div className="text-[10px] md:text-xs text-gray-500 mt-1 truncate">
                  {totalPositionSize ? `${totalPositionSize.toFixed(2)} total entry` : 'No entry amount'}
                </div>
              </div>
              <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
                <div className="text-xs md:text-sm text-gray-500 mb-1">{t('analytics.expectancy')}</div>
                <div className={`text-2xl md:text-3xl font-bold ${expectancy !== null ? (expectancy >= 0 ? 'text-green-600' : 'text-red-600') : 'text-gray-500'}`}>
                  {expectancy !== null ? `${expectancy >= 0 ? '+' : ''}${expectancy.toFixed(2)}R` : '-'}
                </div>
                <div className="text-[10px] md:text-xs text-gray-500 mt-1">
                  {tradesWithRCount > 0 ? t('analytics.expectancyTradesHint', { count: tradesWithRCount }) : t('analytics.expectancyNoData')}
                </div>
              </div>
              <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
                <div className="text-xs md:text-sm text-gray-500 mb-1">{t('analytics.plannedVsRealizedR')}</div>
                <div className={`text-2xl md:text-3xl font-bold ${plannedVsRealizedDiff !== null ? (plannedVsRealizedDiff >= 0 ? 'text-green-600' : 'text-red-600') : 'text-gray-500'}`}>
                  {plannedVsRealizedDiff !== null ? `${plannedVsRealizedDiff >= 0 ? '+' : ''}${plannedVsRealizedDiff.toFixed(2)}R` : '-'}
                </div>
                <div className="text-[10px] md:text-xs text-gray-500 mt-1">
                  {tradesWithPlannedAndRealized.length > 0 ? t('analytics.plannedVsRealizedHint', { count: tradesWithPlannedAndRealized.length }) : t('analytics.plannedVsRealizedNoData')}
                </div>
              </div>
              <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
                <div className="text-xs md:text-sm text-gray-500 mb-1">{t('analytics.stopHitRate')}</div>
                <div className="text-2xl md:text-3xl font-bold text-gray-900">
                  {stopHitRate !== null ? `${stopHitRate.toFixed(1)}%` : '-'}
                </div>
                <div className="text-[10px] md:text-xs text-gray-500 mt-1">
                  {totalTrades > 0 ? `${tradesThatHitStop.length} / ${totalTrades}` : '-'}
                </div>
              </div>
              <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
                <div className="text-xs md:text-sm text-gray-500 mb-1">{t('analytics.avgExitSlDiff')}</div>
                <div className={`text-2xl md:text-3xl font-bold ${avgExitSlDiffPct !== null ? (avgExitSlDiffPct >= 0 ? 'text-green-600' : 'text-red-600') : 'text-gray-500'}`}>
                  {avgExitSlDiffPct !== null ? `${avgExitSlDiffPct >= 0 ? '+' : ''}${avgExitSlDiffPct.toFixed(2)}%` : '-'}
                </div>
                <div className="text-[10px] md:text-xs text-gray-500 mt-1">
                  {tradesWithExitSl.length > 0 ? t('analytics.avgExitSlDiffHint', { count: tradesWithExitSl.length }) : '-'}
                </div>
              </div>
            </div>

            {/* Performance Metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6 text-center sm:text-left">
                <div className="text-xs md:text-sm text-gray-500 mb-1">Average Win</div>
                <div className="text-xl md:text-2xl font-bold text-green-600">{formatCurrency(avgWin)}</div>
              </div>
              <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6 text-center sm:text-left">
                <div className="text-xs md:text-sm text-gray-500 mb-1">Average Loss</div>
                <div className="text-xl md:text-2xl font-bold text-red-600">{formatCurrency(avgLoss)}</div>
              </div>
              <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6 text-center sm:text-left">
                <div className="text-xs md:text-sm text-gray-500 mb-1">Average R:R</div>
                <div className="text-xl md:text-2xl font-bold text-gray-900">{avgRR.toFixed(2)}</div>
              </div>
            </div>

            {/* Best & Worst Trades */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
                <h3 className="text-base md:text-lg font-semibold mb-4 text-center md:text-left">Best Trade</h3>
                {bestTrade ? (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-xs md:text-sm text-gray-500">Symbol</span>
                      <span className="text-xs md:text-sm font-medium">{bestTrade.symbol}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs md:text-sm text-gray-500">P&L</span>
                      <span className="text-xs md:text-sm font-bold text-green-600">
                        {formatCurrency(bestTrade.convertedPnl)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs md:text-sm text-gray-500">Direction</span>
                      <span className={`text-[10px] md:text-xs px-2 py-0.5 rounded ${
                        bestTrade.long_short === 'long' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {bestTrade.long_short.toUpperCase()}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs md:text-sm text-gray-500 text-center">No data</p>
                )}
              </div>
              <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
                <h3 className="text-base md:text-lg font-semibold mb-4 text-center md:text-left">Worst Trade</h3>
                {worstTrade ? (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-xs md:text-sm text-gray-500">Symbol</span>
                      <span className="text-xs md:text-sm font-medium">{worstTrade.symbol}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs md:text-sm text-gray-500">P&L</span>
                      <span className="text-xs md:text-sm font-bold text-red-600">
                        {formatCurrency(worstTrade.convertedPnl)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs md:text-sm text-gray-500">Direction</span>
                      <span className={`text-[10px] md:text-xs px-2 py-0.5 rounded ${
                        worstTrade.long_short === 'long' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {worstTrade.long_short.toUpperCase()}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs md:text-sm text-gray-500 text-center">No data</p>
                )}
              </div>
            </div>

            {/* Market Breakdown */}
            {Object.keys(marketBreakdown).length > 0 && (
              <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
                <h3 className="text-base md:text-lg font-semibold mb-4 text-center md:text-left">{t('analytics.performanceByMarket')}</h3>
                <div className="space-y-3">
                  {Object.entries(marketBreakdown).map(([market, data]) => (
                    <div key={market} className="border-b border-gray-100 last:border-b-0 pb-3 last:pb-0">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm md:text-base font-medium">{market}</span>
                        <span className={`text-sm md:text-base font-semibold ${
                          data.pnl >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {formatCurrency(data.pnl)}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] md:text-xs text-gray-500">
                        <span>{data.count} trades</span>
                        <span>{data.wins}W / {data.losses}L</span>
                        <span>Win Rate: {data.count > 0 ? ((data.wins / data.count) * 100).toFixed(1) : 0}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Long/Short Breakdown */}
            {Object.keys(longShortBreakdown).length > 0 && (
              <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
                <h3 className="text-base md:text-lg font-semibold mb-4 text-center md:text-left">{t('analytics.performanceByDirection')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {Object.entries(longShortBreakdown).map(([direction, data]) => (
                    <div key={direction} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-3">
                        <span className={`text-[10px] md:text-xs font-semibold px-2 py-1 rounded ${
                          direction === 'long' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {direction.toUpperCase()}
                        </span>
                        <span className={`text-sm md:text-base font-semibold ${
                          data.pnl >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {formatCurrency(data.pnl)}
                        </span>
                      </div>
                      <div className="flex flex-col gap-1 text-[10px] md:text-xs text-gray-500">
                        <div className="flex justify-between">
                          <span>Trades:</span>
                          <span>{data.count}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>W/L:</span>
                          <span>{data.wins}W / {data.losses}L</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Win Rate:</span>
                          <span>{data.count > 0 ? ((data.wins / data.count) * 100).toFixed(1) : 0}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Filtered Trades List */}
            <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
              <h3 className="text-base md:text-lg font-semibold mb-4">{t('analytics.filteredTrades')}</h3>
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 font-semibold text-gray-700 whitespace-nowrap">{t('tradeList.symbol')}</th>
                      <th className="px-3 py-2 font-semibold text-gray-700 whitespace-nowrap">{t('addTrade.entryDate')}</th>
                      <th className="px-3 py-2 font-semibold text-gray-700 whitespace-nowrap">{t('tradeList.status')}</th>
                      <th className="px-3 py-2 font-semibold text-gray-700 whitespace-nowrap">{t('tradeList.pnl')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {filteredTrades.map((trade) => {
                      const isOpen = !trade.exit_date
                      const pnlVal = isOpen ? trade.unrealized_pnl : convertPnl(trade)
                      return (
                        <tr
                          key={trade.id}
                          onClick={(e) => handleNavigateToDetail(trade, e)}
                          className="cursor-pointer hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">{trade.symbol}</td>
                          <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                            {trade.entry_date ? new Date(trade.entry_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${isOpen ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                              {isOpen ? t('common:status.open') : t('common:status.closed')}
                            </span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {pnlVal != null ? (
                              <span className={pnlVal >= 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                                {formatCurrency(pnlVal)}
                              </span>
                            ) : '-'}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
        </W>
        );
      })()}

      <Footer currentPage="analytics" onNavigate={(page) => {
        if (page === "privacy-policy") navigate("/privacy");
        else if (page === "terms-and-conditions") navigate("/terms");
        else if (page === "risk-warning") navigate("/risk-warning");
        else if (page === "documentation") navigate("/documentation");
        else if (page === "contact") navigate("/contact");
        else if (page === "home") onBack();
        else if (page === "changelog") setCurrentPage("changelog");
      }} />

      {!embedded && showLogin && (
        <Login
          onClose={() => setShowLogin(false)}
          onSwitchToRegister={() => {
            setShowLogin(false)
            setShowRegister(true)
          }}
        />
      )}

      {!embedded && showRegister && (
        <Register
          onClose={() => setShowRegister(false)}
          onSwitchToLogin={() => {
            setShowRegister(false)
            setShowLogin(true)
          }}
        />
      )}

    </div>
  )
}

export default Analytics

